// src/components/avatar/avatarCatalog.js
//
// What the avatar maker sells, and for how much. The list itself is
// avatarCatalog.json — the SAME file is in functions/avatarCatalog.json,
// where the server reads the prices (the browser never decides a price).
// Edit both together.
//
//   kinds: color (clothes colours), top (clothes), haircolor,
//          glasses, sunglasses, pin, pants, shoes, mustache
//   Picture items (jackets & shirts, shoes, mustaches & beards) have an
//   `img` and a `level` 1–4 — avatarParts.js draws them.
//   price 0 = free for everyone. Educators get everything free.

import data from "./avatarCatalog.json";
import seasonData from "./seasons/seasonCatalog.json";

// + the Seasons items the maker sells (clothes, glasses, sunglasses, pins,
// hair decorations, backgrounds) — they only show inside a season.
export const CATALOG = [...data.items, ...seasonData.items.filter((i) => !["hat", "wings", "pet", "frame"].includes(i.kind))];
export const CATALOG_BY_ID = Object.fromEntries(CATALOG.map((i) => [i.id, i]));
const BUILT_IN_IDS = new Set(CATALOG.map((i) => i.id));
/** the everyday (non-season) items of a kind */
export const itemsOfKind = (kind) => CATALOG.filter((i) => i.kind === kind && !i.season);
/** items a student may see in the maker: not hidden, or already theirs / being worn */
export const shownItemsOfKind = (kind, owned, keep = []) => itemsOfKind(kind).filter((i) => !isHiddenItem(i.id) || (owned && owned.has(i.id)) || keep.includes(i.id));
// Admin overrides (Layout Builder → Rewards & avatar settings), filled in
// live by services/rewardSettingsService.js. The server applies the same.
const overrides = { prices: {}, hidden: {} };
export const setAvatarOverrides = (avatar) => {
  overrides.prices = (avatar && avatar.prices) || {};
  overrides.hidden = (avatar && avatar.hidden) || {};
};

export const basePriceOf = (id) => (id && CATALOG_BY_ID[id] ? Number(CATALOG_BY_ID[id].price) || 0 : 0);
export const priceOf = (id) => {
  const it = id && CATALOG_BY_ID[id];
  if (!it) return 0;
  if (it.exclusive) return Number(it.price) || 0;
  const raw = overrides.prices[id];
  const o = raw === undefined || raw === null || raw === "" ? NaN : Math.floor(Number(raw));
  return Number.isFinite(o) && o >= 0 ? o : Number(it.price) || 0;
};
export const isHiddenItem = (id) => Boolean(overrides.hidden[id] || (id && CATALOG_BY_ID[id]?.hidden));

// ---------------------------------------------------------------------------
// UPLOADED ITEMS (Layout Builder → Rewards & avatar settings → Upload avatar
// items). They live in Firestore avatarItems/{id} ("av-up-…"), not in the
// JSON, and are added here as they arrive (services/avatarItemsService.js),
// so a new pair of sneakers shows up without a code change. Same fields as
// the picture items in the JSON (img, ar, sh, hem, fit, layer…), plus
// `season` for a holiday item.
// ---------------------------------------------------------------------------
let liveIds = new Set();
let itemsVersion = 0;
const itemsSubs = new Set();
const bumpItems = () => {
  itemsVersion += 1;
  itemsSubs.forEach((f) => f(itemsVersion));
};
export const avatarItemsVersion = () => itemsVersion;
export const onAvatarItemsChange = (fn) => {
  itemsSubs.add(fn);
  return () => itemsSubs.delete(fn);
};

/** Replace the uploaded items with this list (from the live listener). */
export const setLiveAvatarItems = (list = []) => {
  for (let i = CATALOG.length - 1; i >= 0; i -= 1) if (liveIds.has(CATALOG[i].id)) CATALOG.splice(i, 1);
  liveIds.forEach((id) => {
    if (CATALOG_BY_ID[id]?.live) delete CATALOG_BY_ID[id];
  });
  liveIds = new Set();
  [...list]
    .filter((it) => it && it.id && it.img && it.active !== false && !BUILT_IN_IDS.has(it.id))
    .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
    .forEach((it) => {
      const item = { ...it, live: true };
      CATALOG.push(item);
      CATALOG_BY_ID[item.id] = item;
      liveIds.add(item.id);
    });
  bumpItems();
};

/** Try a not-yet-saved item on the avatar (the upload screen's preview). */
export const previewAvatarItem = (item) => {
  if (!item?.id) return;
  CATALOG_BY_ID[item.id] = { ...item, live: true, preview: true };
  bumpItems();
};

/** True if the student may use this part: free, owned, or an educator. */
export const canUse = (id, owned, free = false) => !id || free || priceOf(id) === 0 || (owned && owned.has(id));

export const LEVEL_NAMES = {
  glasses: { 1: "Everyday Classics", 2: "Fun & Colourful", 3: "Stylish & Trendy", 4: "Fancy & Unique" },
  sunglasses: { 1: "Everyday Classics", 2: "Cool & Colourful", 3: "Super Cool", 4: "Fancy Bling" },
  top: { 1: "Basic Everyday", 2: "Cool Everyday Wear", 3: "Stylish & Trendy", 4: "Statement & Special" },
  shoes: { 1: "Basic Everyday", 2: "Popular Styles", 3: "Trendy & Athletic", 4: "Premium & Special" },
  mustache: { 1: "Simple & Light", 2: "Classic Styles", 3: "Fuller Looks", 4: "Bold & Special" },
};
